import React, { useEffect, useState } from "react";
import { Trash2, Plus, Car as VehicleIcon, Layers, Package, ChevronDown, Search as SearchIcon, ExternalLink } from "lucide-react";
import {
  jobTypeMeta, wrapAreasFor, ppfAreasFor, tintWindowsFor, CONDITION_OPTIONS,
  LENS_TINT_AREAS, LEGACY_INSTALLATION_TYPES, BADGE_SPECIFICS,
  INSTALLATION_TYPES, installationMeta, blankInstallation, isVehicleJobType,
  enabledInstallationTypes,
} from "../lib/jobTypes";
import { api } from "../lib/api";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { useCustomAreas, mergeAreas } from "../hooks/useCustomAreas";

/**
 * A job_item now models ONE vehicle (or object) with ONE OR MORE
 * installations happening on it. The vehicle block (year/make/model/
 * condition/body-kit) is entered once at the top, and each installation
 * gets its own sub-card below with its own installation_type + fields.
 *
 * This lets a customer bringing in a car for "full colour change + window
 * tint" record both installs against the same vehicle without retyping.
 */
export default function JobItemForm({ item, index, onPatch, onRemove, showQuoteSeparately = false }) {
  const meta = jobTypeMeta(item.job_type);
  const Icon = meta.icon;
  const installations = item.installations && item.installations.length > 0
    ? item.installations
    : [blankInstallation()];

  // Load stocked materials once so every installation's Material field can
  // offer a "pick from inventory" dropdown right next to the text input.
  // Silent on failure so leads / quotes forms (used by non-admins) still work
  // even when the /materials endpoint isn't reachable.
  const [materialsList, setMaterialsList] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/materials")
      .then((r) => { if (!cancelled) setMaterialsList(r.data || []); })
      .catch(() => { if (!cancelled) setMaterialsList([]); });
    return () => { cancelled = true; };
  }, []);

  const totalPrice = installations.reduce((s, inst) => s + (Number(inst.price) || 0), 0);

  const patchInstallation = (installIdx, patch) => {
    onPatch({
      installations: installations.map((inst, i) => i === installIdx ? { ...inst, ...patch } : inst),
    });
  };

  const addInstallation = () => {
    onPatch({ installations: [...installations, blankInstallation()] });
  };

  const removeInstallation = (installIdx) => {
    if (installations.length <= 1) return;
    onPatch({ installations: installations.filter((_, i) => i !== installIdx) });
  };

  return (
    <div
      data-testid={`job-item-${index}`}
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-4"
    >
      {/* Header: vehicle-level identity + total price + remove button */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-white">
          <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center">
            <Icon className="w-3.5 h-3.5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Item {index + 1}</div>
            <div className="text-sm font-medium">{meta.label}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-black/40 border border-white/10 rounded-xl px-2.5 py-1.5">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">Total $</span>
            <span data-testid={`item-${index}-price`} className="text-sm text-white w-16 text-right">
              {totalPrice.toLocaleString()}
            </span>
          </div>
          <button
            type="button"
            data-testid={`job-item-remove-${index}`}
            onClick={onRemove}
            className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-300"
            title="Remove this item"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* "Quote this item as a separate option" tickbox — only shown in
          the Quote editor (not Job cards), controlled by the parent via
          `showQuoteSeparately`. When ticked, the customer-facing quote
          shows this item as its own standalone option card instead of
          lumping it into the per-vehicle breakdown. Great for shops
          offering a customer two alternatives on the same car (e.g.
          "Full Wrap option" vs "PPF option") so the client sees each
          price clearly rather than a single combined total. */}
      {showQuoteSeparately && (
        <label
          className="flex items-start gap-2 rounded-xl border border-sky-400/40 bg-sky-50 dark:bg-sky-500/[0.08] px-3 py-2 cursor-pointer select-none hover:bg-sky-100 dark:hover:bg-sky-500/[0.14] transition"
          title="Shown as a separate priced option on the customer-facing quote. Grand total at the bottom still includes all items."
        >
          <input
            type="checkbox"
            data-testid={`job-item-${index}-quote-separately`}
            checked={!!item.quote_separately}
            onChange={(e) => onPatch({ quote_separately: e.target.checked })}
            className="w-4 h-4 accent-sky-500 mt-0.5 shrink-0"
          />
          <div className="min-w-0">
            <div className="text-xs text-sky-900 dark:text-white font-semibold">
              Quote this item as a separate option
            </div>
            <div className="text-[11px] text-sky-800/90 dark:text-sky-100/80 leading-snug">
              Customer sees this on its own card with its own price — perfect for showing two alternatives (e.g. Wrap vs PPF) side-by-side.
            </div>
          </div>
        </label>
      )}

      {/* Vehicle block — year/make/model/condition entered ONCE per item.
          Only shown for vehicle-style types (car, boat, motorbike, etc.).
          Non-vehicles (fridge, signage, building, "other") swap this for a
          single "Item description" text box because Year/Make/Model is
          meaningless there. */}
      {isVehicleJobType(item.job_type) ? (
        <div className="rounded-2xl bg-black/25 border border-white/5 p-3 space-y-3">
          <div className="text-label flex items-center gap-1.5"><VehicleIcon className="w-3 h-3" /> Vehicle details</div>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Year" value={item.year} onChange={(v) => onPatch({ year: v })} testId={`item-${index}-year`} />
            <Field label="Make" value={item.make} onChange={(v) => onPatch({ make: v })} testId={`item-${index}-make`} />
            <Field label="Model" value={item.model} onChange={(v) => onPatch({ model: v })} testId={`item-${index}-model`} />
          </div>
          <VehicleSizeChip make={item.make} model={item.model} itemType={item.job_type} index={index} />
          <div>
            <label className="text-label block mb-1.5">Condition</label>
            <select
              data-testid={`item-${index}-condition`}
              value={item.condition || ""}
              onChange={(e) => onPatch({ condition: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30"
            >
              <option value="" className="bg-neutral-900">Select condition…</option>
              {CONDITION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className="bg-neutral-900">{o.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`item-${index}-body-kit`}
              data-testid={`item-${index}-body-kit`}
              checked={!!item.body_kit}
              onChange={(e) => onPatch({ body_kit: e.target.checked })}
              className="w-4 h-4 accent-emerald-400"
            />
            <label htmlFor={`item-${index}-body-kit`} className="text-sm text-slate-200">Vehicle has a body kit</label>
          </div>
          {item.body_kit && (
            <Field
              label="Body kit details"
              value={item.body_kit_notes}
              onChange={(v) => onPatch({ body_kit_notes: v })}
              testId={`item-${index}-body-kit-notes`}
              placeholder="e.g. Rocket Bunny widebody, custom diffuser"
            />
          )}
        </div>
      ) : (
        <div className="rounded-2xl bg-black/25 border border-white/5 p-3 space-y-2">
          <div className="text-label flex items-center gap-1.5"><Package className="w-3 h-3" /> {meta.itemLabel || "Item"} details</div>
          <Field
            label="Item description"
            value={item.item_description || item.make || ""}
            onChange={(v) => onPatch({ item_description: v, make: v })}
            testId={`item-${index}-item-description`}
            placeholder={meta.placeholder || "Describe the item…"}
          />
        </div>
      )}

      {/* Installations — one or more services on this same vehicle */}
      <div className="space-y-3">
        {installations.map((inst, installIdx) => (
          <InstallationBlock
            key={inst.id || installIdx}
            installation={inst}
            index={index}
            installIdx={installIdx}
            canRemove={installations.length > 1}
            materialsList={materialsList}
            parentJobType={item.job_type}
            siblingTypes={installations.map((s) => s.installation_type).filter(Boolean)}
            onPatch={(patch) => patchInstallation(installIdx, patch)}
            onRemove={() => removeInstallation(installIdx)}
            onAddCeramic={() => onPatch({
              // Append a ceramic install pre-filled so the office confirms
              // upsell in one tap. Uses blankInstallation() defaults for
              // structure so it interops with the pipeline stage picker.
              installations: [...installations, { ...blankInstallation(), installation_type: "ceramic" }],
            })}
          />
        ))}
      </div>

      {/* Add another installation for THIS same vehicle */}
      <button
        type="button"
        data-testid={`item-${index}-add-install`}
        onClick={addInstallation}
        className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-blue-700 dark:text-blue-200 bg-blue-500/15 hover:bg-blue-500/25 border border-blue-400/30 rounded-xl py-2.5"
      >
        <Plus className="w-3.5 h-3.5" />
        Add another installation for this {isVehicleJobType(item.job_type) ? "vehicle" : "item"}
      </button>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/*                       Installation sub-block                            */
/* ----------------------------------------------------------------------- */
function InstallationBlock({ installation, index, installIdx, canRemove, materialsList = [], parentJobType, siblingTypes = [], onPatch, onRemove, onAddCeramic }) {
  const im = installationMeta(installation.installation_type);
  const kind = im?.form_kind || "notes";
  const isWrap = kind === "wrap";
  const isPPF = kind === "ppf";
  const isLensTint = kind === "lens_tint";
  const isTint = kind === "tint";                 // window tinting w/ per-window picker
  const isBadge = kind === "badge";
  const isBusinessBranding = installation.installation_type === "wrap_business";

  // Auto-migrate legacy lens-tint values (headlight_tint / taillight_tint /
  // lens_tint_full_set) to the unified `lens_tint` type the moment the form
  // touches this installation. Sets `lens_area` from the legacy default_area
  // so nothing is lost. Runs once per legacy record.
  useEffect(() => {
    const legacy = LEGACY_INSTALLATION_TYPES[installation.installation_type];
    if (!legacy) return;
    onPatch({
      installation_type: "lens_tint",
      lens_area: installation.lens_area || legacy.default_area,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installation.installation_type]);
  // Areas of application adapt to the parent item's job_type — a motorbike
  // job shows tank / fairings / swingarm; a boat shows hull / transom;
  // a caravan shows front panel / storage hatches; and so on.
  const customAreas = useCustomAreas();
  const wrapAreaOptions = mergeAreas(wrapAreasFor(parentJobType), customAreas, parentJobType, "wrap");
  const ppfAreaOptions = mergeAreas(ppfAreasFor(parentJobType), customAreas, parentJobType, "ppf");
  // Window-tinting is NOT user-customisable via the Settings → custom
  // areas feature right now — the catalogue is already granular enough
  // (every pane of glass is listed). If a shop asks later, we can let
  // them add extras the same way wrap/PPF do via mergeAreas.
  const tintWindowOptions = tintWindowsFor(parentJobType);
  // Human-readable hint next to the field label so the office knows why
  // the option list changed.
  const areaHint = parentJobType && parentJobType !== "car"
    ? (jobTypeMeta(parentJobType)?.itemLabel || parentJobType).toLowerCase()
    : null;

  return (
    <div
      data-testid={`item-${index}-install-${installIdx}`}
      className="rounded-2xl bg-white/[0.02] border border-white/10 p-3 space-y-3"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-white/10 flex items-center justify-center">
            <Layers className="w-3 h-3 text-slate-300" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Installation {installIdx + 1}</div>
            <div className="text-xs text-white">
              {im?.label || <span className="text-slate-500 italic">Pick a service…</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Estimated install hours — required by auto-quote so Claude has
              an hourly rate to work with. Also feeds the visual progress
              guide rendered on the job card. */}
          <div className="flex items-center gap-1.5 bg-black/40 border border-white/10 rounded-xl px-2 py-1" title="Estimated install time (hours)">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">Hrs</span>
            <input
              type="number"
              min="0"
              step="0.5"
              value={installation.estimated_hours || ""}
              onChange={(e) => onPatch({ estimated_hours: Number(e.target.value) || 0 })}
              data-testid={`item-${index}-install-${installIdx}-hours`}
              className="w-14 bg-transparent text-xs text-white placeholder-slate-500 focus:outline-none text-right"
              placeholder="0"
            />
          </div>
          {/* Price cluster — the outside border glows yellow when the
              installation is flagged as an estimate so it's obvious at a
              glance which figures aren't locked in yet. */}
          <div className={`flex flex-col items-end gap-1 ${installation.is_estimate ? "" : ""}`}>
            <div className={`flex items-center gap-1.5 bg-black/40 rounded-xl px-2 py-1 border ${installation.is_estimate ? "border-amber-400 ring-2 ring-amber-400/40" : "border-white/10"}`}>
              <span className="text-[10px] uppercase tracking-widest text-slate-500">$</span>
              <input
                type="number"
                min="0"
                step="1"
                value={installation.price || ""}
                onChange={(e) => onPatch({ price: Number(e.target.value) || 0 })}
                data-testid={`item-${index}-install-${installIdx}-price`}
                className="w-20 bg-transparent text-xs text-white placeholder-slate-500 focus:outline-none text-right"
                placeholder="0"
              />
            </div>
            {installation.is_estimate && (
              <div
                className="flex items-center gap-1.5 bg-black/40 border border-amber-400 ring-2 ring-amber-400/40 rounded-xl px-2 py-1"
                title="Upper end of the estimate range (leave blank if you only want a floor)"
              >
                <span className="text-[10px] uppercase tracking-widest text-amber-300">To $</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={installation.price_max || ""}
                  onChange={(e) => onPatch({ price_max: Number(e.target.value) || 0 })}
                  data-testid={`item-${index}-install-${installIdx}-price-max`}
                  className="w-20 bg-transparent text-xs text-white placeholder-slate-500 focus:outline-none text-right"
                  placeholder="range max"
                />
              </div>
            )}
            <label
              className="flex items-center gap-1.5 cursor-pointer select-none pr-1"
              title="Ticks this installation as a rough estimate — customer-facing quote shows a range instead of a fixed number."
            >
              <input
                type="checkbox"
                checked={!!installation.is_estimate}
                onChange={(e) => onPatch({
                  is_estimate: e.target.checked,
                  // Clear the range max when un-ticking so stale data doesn't linger
                  ...(e.target.checked ? {} : { price_max: 0 }),
                })}
                data-testid={`item-${index}-install-${installIdx}-is-estimate`}
                className="w-3 h-3 accent-amber-400 cursor-pointer"
              />
              <span className={`text-[10px] uppercase tracking-widest ${installation.is_estimate ? "text-amber-300" : "text-slate-500"}`}>
                Estimate Price
              </span>
            </label>
          </div>
          {canRemove && (
            <button
              type="button"
              data-testid={`item-${index}-install-${installIdx}-remove`}
              onClick={onRemove}
              className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 text-slate-400 hover:text-red-300"
              title="Remove this installation"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Installation type picker — searchable so admins with 20+ types
          (wraps, PPF variants, 6 detail sub-types, tint, ceramic, signage,
          "other") can filter down instantly instead of scanning the list. */}
      <div>
        <label className="text-label block mb-1.5">Installation type</label>
        <InstallationTypeSearch
          testId={`item-${index}-install-${installIdx}-type`}
          value={installation.installation_type || ""}
          onChange={(v) => onPatch({ installation_type: v })}
        />
      </div>

      {/* Ceramic upsell nudge — when the office picks a service that pairs
          naturally with ceramic coating (Paint Correction, Full Detail, or
          any wrap that leaves the paint prepped), suggest adding it as its
          own installation. Hidden once ceramic is already on the item so
          the office never sees it twice. */}
      {installation.installation_type && onAddCeramic && shouldSuggestCeramic(installation.installation_type, siblingTypes) && (
        <div
          data-testid={`item-${index}-install-${installIdx}-ceramic-upsell`}
          className="rounded-2xl border border-cyan-400/30 bg-cyan-500/[0.08] p-3 flex items-start gap-3"
        >
          <div className="shrink-0 w-8 h-8 rounded-xl bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center text-lg">✨</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white font-medium">Pair this with a ceramic coating?</div>
            <div className="text-[11px] text-cyan-100/80 mt-0.5">
              The paint is already prepped for {installationMeta(installation.installation_type)?.label || "this service"} — sealing it with ceramic locks in the finish and is a common upsell.
            </div>
          </div>
          <button
            type="button"
            data-testid={`item-${index}-install-${installIdx}-ceramic-add`}
            onClick={onAddCeramic}
            className="shrink-0 text-xs font-semibold bg-cyan-400 text-black px-3 py-1.5 rounded-full hover:bg-cyan-300"
          >
            Add ceramic
          </button>
        </div>
      )}

      {/* Business branding design workflow — only for wrap_business */}
      {isBusinessBranding && (
        <div
          data-testid={`item-${index}-install-${installIdx}-design-panel`}
          className="rounded-2xl border border-violet-400/25 bg-violet-500/[0.06] p-3 space-y-3"
        >
          <div className="flex items-center justify-between">
            <div className="text-label text-violet-200">Design</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              Business branding requires artwork
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              { value: "provided", label: "Customer provided", hint: "Print-ready files supplied" },
              { value: "inhouse",  label: "Design in-house",   hint: "We create it — adds design stages" },
            ].map((opt) => {
              const active = installation.design_source === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`item-${index}-install-${installIdx}-design-source-${opt.value}`}
                  onClick={() => onPatch({
                    design_source: opt.value,
                    design_status: opt.value === "inhouse" ? (installation.design_status || "awaiting_brief") : "",
                  })}
                  className={`text-left rounded-xl border px-3 py-2 transition ${
                    active
                      ? "bg-violet-500/25 border-violet-400/60 text-white"
                      : "bg-black/25 border-white/10 text-slate-300 hover:bg-white/[0.06]"
                  }`}
                >
                  <div className="text-sm font-medium">{opt.label}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{opt.hint}</div>
                </button>
              );
            })}
          </div>

          {installation.design_source === "inhouse" && (
            <div>
              <label className="text-label block mb-1.5">Current design status</label>
              <select
                data-testid={`item-${index}-install-${installIdx}-design-status`}
                value={installation.design_status || "awaiting_brief"}
                onChange={(e) => onPatch({ design_status: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
              >
                <option value="awaiting_brief"    className="bg-neutral-900">Awaiting brief from customer</option>
                <option value="in_progress"       className="bg-neutral-900">Design in progress</option>
                <option value="awaiting_approval" className="bg-neutral-900">Awaiting customer approval</option>
                <option value="approved"          className="bg-neutral-900">Approved — ready to print</option>
              </select>
            </div>
          )}

          {(installation.design_source === "provided" || installation.design_source === "inhouse") && (
            <div>
              <label className="text-label block mb-1.5">Design notes</label>
              <textarea
                data-testid={`item-${index}-install-${installIdx}-design-notes`}
                value={installation.design_notes || ""}
                onChange={(e) => onPatch({ design_notes: e.target.value })}
                rows={2}
                placeholder={installation.design_source === "provided"
                  ? "Link to files / dropbox, colour codes, print resolution notes…"
                  : "Brand guidelines link, colour palette, must-have logos, dimensions…"}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
              />
            </div>
          )}
        </div>
      )}

      {/* Wrap-specific fields */}
      {isWrap && (
        <>
          <MultiSelectDropdown
            label={areaHint ? `Areas of wrap application — ${areaHint}` : "Areas of wrap application"}
            testId={`item-${index}-install-${installIdx}-wrap-areas`}
            options={wrapAreaOptions}
            value={installation.wrap_areas || []}
            onChange={(v) => onPatch({ wrap_areas: v })}
            placeholder="Pick wrap areas…"
            chipGridOnMobile
          />
          <AreaComboHint
            jobType={parentJobType}
            installationType={installation.installation_type}
            areas={installation.wrap_areas || []}
            testId={`item-${index}-install-${installIdx}-wrap-hint`}
          />
        </>
      )}

      {/* PPF-specific fields */}
      {isPPF && (
        <>
          <MultiSelectDropdown
            label={areaHint ? `Installation areas — ${areaHint}` : "Installation areas"}
            testId={`item-${index}-install-${installIdx}-ppf-areas`}
            options={ppfAreaOptions}
            value={installation.ppf_areas || []}
            onChange={(v) => onPatch({ ppf_areas: v })}
            placeholder="Pick installation areas…"
            chipGridOnMobile
          />
          <AreaComboHint
            jobType={parentJobType}
            installationType={installation.installation_type}
            areas={installation.ppf_areas || []}
            testId={`item-${index}-install-${installIdx}-ppf-hint`}
          />
        </>
      )}

      {/* Window-tint windows picker — one tick per pane of glass so the
          office captures scope precisely and installers know exactly
          what's in-scope before they start cutting film. */}
      {isTint && (
        <MultiSelectDropdown
          label={areaHint ? `Windows to tint — ${areaHint}` : "Windows to tint"}
          testId={`item-${index}-install-${installIdx}-tint-windows`}
          options={tintWindowOptions}
          value={installation.tint_areas || []}
          onChange={(v) => onPatch({ tint_areas: v })}
          placeholder="Pick windows…"
          chipGridOnMobile
        />
      )}

      {/* Lens-tint specific fields — single-select segmented picker for
          the application area (full set vs headlight/taillight only vs
          combo w/ reflectors). Notes still available below for anything
          custom the shop wants to record. */}
      {isLensTint && (
        <div className="space-y-1.5">
          <label className="text-label block">Area of application</label>
          <div
            data-testid={`item-${index}-install-${installIdx}-lens-area`}
            className="flex flex-wrap gap-1.5"
          >
            {LENS_TINT_AREAS.map((opt) => {
              const active = installation.lens_area === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`item-${index}-install-${installIdx}-lens-area-${opt.value}`}
                  onClick={() => onPatch({ lens_area: opt.value })}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    active
                      ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/40"
                      : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {!installation.lens_area && (
            <p className="text-[11px] text-amber-300/80">Pick the area the lens tint will cover.</p>
          )}
        </div>
      )}

      {/* Badge-modification specific fields — multi-select pill toggles for
          the exact pieces of work the shop will do on this vehicle. Multiple
          selections are common (e.g. Badge delete + Badge blackout on the
          same car), so the array pattern is used here rather than the
          single-value pill used by lens-tint. */}
      {isBadge && (
        <div className="space-y-1.5">
          <label className="text-label block">Installation specifics</label>
          <div
            data-testid={`item-${index}-install-${installIdx}-badge-specifics`}
            className="flex flex-wrap gap-1.5"
          >
            {BADGE_SPECIFICS.map((opt) => {
              const list = installation.badge_specifics || [];
              const active = list.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`item-${index}-install-${installIdx}-badge-${opt.value}`}
                  onClick={() => {
                    const next = active
                      ? list.filter((v) => v !== opt.value)
                      : [...list, opt.value];
                    onPatch({ badge_specifics: next });
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    active
                      ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/40"
                      : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {(installation.badge_specifics || []).length === 0 && (
            <p className="text-[11px] text-amber-300/80">Pick at least one — you can select multiple.</p>
          )}
        </div>
      )}

      {/* Unified searchable Material field — surfaces both local inventory
          (INV badge) and Vinyl Wrapping Australia Shopify catalog (VWA badge)
          in a single picker. Brand is captured inside the material label, so
          the old separate "PPF brand" dropdown was retired here. Free-typed
          values still fall through to the Inventory "Needs to be ordered"
          panel automatically. */}
      {(isWrap || isPPF) && (
        <MaterialSearchField
          value={installation.material || ""}
          onChange={(v) => onPatch({ material: v })}
          inventory={materialsList}
          isPPF={isPPF}
          testIdBase={`item-${index}-install-${installIdx}-material`}
        />
      )}

      <TextArea
        label="Installation notes"
        value={installation.notes}
        onChange={(v) => onPatch({ notes: v })}
        testId={`item-${index}-install-${installIdx}-notes`}
        rows={2}
        hint="Visible to the customer on their quote — anything they should know about this specific install."
      />
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/*                       Shared field primitives                           */
/* ----------------------------------------------------------------------- */
function Field({ label, value, onChange, testId, placeholder }) {
  return (
    <div>
      <label className="text-label block mb-1.5">{label}</label>
      <input
        data-testid={testId}
        type="text"
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
      />
    </div>
  );
}

function TextArea({ label, value, onChange, testId, placeholder, rows = 3, hint }) {
  return (
    <div>
      <label className="text-label block mb-1.5">{label}</label>
      <textarea
        data-testid={testId}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
      />
      {hint && <p className="text-[11px] text-slate-500 mt-1.5">{hint}</p>}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* MaterialSearchField                                                        */
/* -------------------------------------------------------------------------- */
/**
 * Single searchable Material picker that unifies the shop's local inventory
 * (INV badge) and the Vinyl Wrapping Australia Shopify catalog (VWA badge).
 *
 * - Type to search — inventory filters instantly client-side, VWA products
 *   are fetched via `/api/shopify/products?q=` with a light debounce.
 * - Free-typed values are preserved; picking a row overwrites with a clean
 *   brand+name label so it looks tidy on quotes / invoices.
 * - Silently degrades if the Shopify integration isn't configured or the
 *   caller isn't office/admin (endpoint returns 403 → we just skip VWA).
 */
function MaterialSearchField({ value, onChange, inventory = [], isPPF, testIdBase }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [vwa, setVwa] = useState([]);
  const [vwaLoading, setVwaLoading] = useState(false);
  const rootRef = React.useRef(null);
  const debounceRef = React.useRef(null);

  // Close on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Debounced VWA search — only when the picker is open to avoid firing
  // on every keystroke while the user is just typing a custom label.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setVwaLoading(true);
    debounceRef.current = setTimeout(() => {
      api.get("/shopify/products", { params: { q: q.trim(), first: 12 } })
        .then((r) => setVwa(r.data?.products || []))
        .catch(() => setVwa([]))
        .finally(() => setVwaLoading(false));
    }, 220);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, open]);

  const needle = q.trim().toLowerCase();
  const invMatches = (inventory || []).filter((m) => {
    if (!needle) return true;
    const hay = `${m.brand || ""} ${m.name || ""} ${m.sku || ""}`.toLowerCase();
    return hay.includes(needle);
  }).slice(0, 20);

  const pickInventory = (m) => {
    const label = joinBrandName(m.brand, m.name);
    onChange(label);
    setOpen(false);
    setQ("");
  };
  const pickVwa = (p) => {
    // Prefer vendor + title with the brand-dedup rule so a Shopify product
    // like `vendor: "3M", title: "3M 1080 Satin Black"` renders as
    // "3M 1080 Satin Black" instead of "3M 3M 1080 Satin Black".
    const label = joinBrandName(p.vendor, p.title) || p.title || "";
    onChange(label);
    setOpen(false);
    setQ("");
  };

  const placeholder = isPPF
    ? "Search STEK, XPEL, SunTek… or type custom"
    : "Search 3M, Avery, Teckwrap… or type custom";

  return (
    <div className="space-y-1.5" ref={rootRef}>
      <label className="text-label block">Material</label>
      <div className="relative">
        <input
          data-testid={testIdBase}
          type="text"
          value={open ? q : (value || "")}
          placeholder={value ? placeholder : placeholder}
          onFocus={() => { setOpen(true); setQ(value || ""); }}
          onChange={(e) => {
            setQ(e.target.value);
            onChange(e.target.value);
            if (!open) setOpen(true);
          }}
          className="w-full bg-black/40 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
        />
        <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>

      {open && (
        <div
          data-testid={`${testIdBase}-dropdown`}
          className="rounded-xl border border-white/10 bg-neutral-950/95 backdrop-blur max-h-72 overflow-y-auto shadow-lg"
        >
          {/* Inventory group */}
          {invMatches.length > 0 && (
            <div className="py-1">
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-slate-500">
                Your inventory
              </div>
              {invMatches.map((m) => (
                <button
                  key={`inv-${m.id}`}
                  type="button"
                  data-testid={`${testIdBase}-opt-inv-${m.id}`}
                  onClick={() => pickInventory(m)}
                  className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-2"
                >
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 shrink-0">INV</span>
                  <span className="text-sm text-white truncate">
                    {joinBrandName(m.brand, m.name)}
                  </span>
                  <span className="ml-auto text-xs text-slate-400 shrink-0">
                    {m.stock_qty} {m.unit}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* VWA group */}
          <div className="py-1 border-t border-white/5">
            <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-slate-500 flex items-center justify-between gap-2">
              <span className="truncate">Order from Vinyl Wrapping Australia</span>
              <div className="flex items-center gap-2 shrink-0">
                {vwaLoading && <span className="text-slate-600 normal-case tracking-normal">searching…</span>}
                <a
                  href="https://vinylwrappingaustralia.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`${testIdBase}-vwa-visit-store`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30 normal-case tracking-normal text-[10px] hover:bg-sky-500/25"
                >
                  Visit store <ExternalLink className="w-2.5 h-2.5" />
                </a>
              </div>
            </div>
            {!vwaLoading && vwa.length === 0 && (
              <div className="px-3 py-2 text-xs text-slate-500">
                {needle ? "No VWA matches." : "Type to search VWA catalog."}
              </div>
            )}
            {vwa.map((p) => (
              <button
                key={`vwa-${p.id}`}
                type="button"
                data-testid={`${testIdBase}-opt-vwa-${p.handle}`}
                onClick={() => pickVwa(p)}
                className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-2"
              >
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-500/30 shrink-0">VWA</span>
                <span className="text-sm text-white truncate">
                  {joinBrandName(p.vendor, p.title) || p.title}
                </span>
              </button>
            ))}
          </div>

          {/* Keep-as-typed hint */}
          {needle && (
            <div className="px-3 py-2 border-t border-white/5 text-xs text-slate-500">
              Or press outside to keep "<span className="text-slate-300">{q}</span>" as custom.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* InstallationTypeSearch                                                     */
/* -------------------------------------------------------------------------- */
/**
 * Searchable combobox for `INSTALLATION_TYPES`. The list is short enough
 * to stay in-memory (client-side filter, no API), but grouped by
 * `form_kind` so wraps / PPF / detailing / notes bubble together for
 * scanability. Clicking outside closes the panel; Esc clears the search.
 */

/**
 * Stitch a brand + product name into a single label while avoiding the
 * "3M 3M 1080 Satin Black" duplication that happens when Shopify vendors
 * (or our own inventory rows) already embed the brand inside the title.
 *
 * Rules:
 *  - Empty brand → just return the name.
 *  - Empty name  → just return the brand.
 *  - If the name already starts with the brand (case-insensitive, word-boundary
 *    aware) → drop the standalone brand prefix.
 *  - Otherwise → "Brand Name".
 */
function joinBrandName(brand, name) {
  const b = (brand || "").trim();
  const n = (name || "").trim();
  if (!b) return n;
  if (!n) return b;
  const lowerName = n.toLowerCase();
  const lowerBrand = b.toLowerCase();
  if (lowerName === lowerBrand) return n;
  // Match at start followed by a space or dash so "3M" doesn't collapse
  // a "3M1080…" (rare but avoids false positives on SKUs).
  if (lowerName.startsWith(lowerBrand + " ") || lowerName.startsWith(lowerBrand + "-")) {
    return n;
  }
  return `${b} ${n}`;
}


function InstallationTypeSearch({ value, onChange, testId }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const rootRef = React.useRef(null);

  const selected = React.useMemo(
    () => INSTALLATION_TYPES.find((o) => o.value === value) || null,
    [value],
  );

  // Reset search whenever the panel closes so re-opening feels fresh.
  React.useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  // Click-outside to close.
  React.useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const filtered = React.useMemo(() => {
    // Show only services the shop offers — but if the current selection
    // is a disabled service (legacy record), keep it visible so nothing
    // vanishes silently on the picker.
    const active = enabledInstallationTypes();
    const activeSet = new Set(active.map((t) => t.value));
    const legacy = value && !activeSet.has(value)
      ? INSTALLATION_TYPES.filter((t) => t.value === value)
      : [];
    const pool = [...active, ...legacy];
    const term = q.trim().toLowerCase();
    if (!term) return pool;
    return pool.filter((o) =>
      o.label.toLowerCase().includes(term) || o.value.includes(term)
    );
  }, [q, value]);

  const pick = (opt) => {
    onChange(opt.value);
    setOpen(false);
  };

  return (
    <div className="relative" ref={rootRef}>
      {/* Trigger — reflects the current selection and opens the panel */}
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((v) => !v)}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-left focus:outline-none focus:border-white/30 flex items-center justify-between gap-2 hover:border-white/20"
      >
        <span className={selected ? "text-white" : "text-slate-500"}>
          {selected ? selected.label : "Select installation service…"}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid={testId ? `${testId}-panel` : undefined}
          className="absolute z-30 mt-1 left-0 right-0 glass-strong border border-white/10 rounded-2xl p-2 shadow-2xl"
        >
          {/* Search box */}
          <div className="relative mb-2">
            <SearchIcon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              autoFocus
              data-testid={testId ? `${testId}-search` : undefined}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") { setOpen(false); }
                if (e.key === "Enter" && filtered.length === 1) { pick(filtered[0]); }
              }}
              placeholder="Search — try wrap, ppf, detail, ceramic…"
              className="w-full bg-black/40 border border-white/10 rounded-lg pl-7 pr-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
            />
          </div>

          <div className="max-h-72 overflow-y-auto pr-1 -mr-1 space-y-0.5">
            {filtered.length === 0 && (
              <div className="text-xs text-slate-500 italic px-2 py-3 text-center">
                No installation type matches &ldquo;{q}&rdquo;
              </div>
            )}
            {filtered.map((opt) => {
              const isSel = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={testId ? `${testId}-opt-${opt.value}` : undefined}
                  onClick={() => pick(opt)}
                  className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-xl text-xs transition ${
                    isSel ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/[0.06]"
                  }`}
                >
                  <span className="flex-1">{opt.label}</span>
                  <span className="text-[9px] uppercase tracking-widest text-slate-500 shrink-0">
                    {opt.form_kind === "wrap" ? "wrap"
                      : opt.form_kind === "ppf" ? "ppf"
                      : "extra"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Ceramic upsell logic                                                       */
/* -------------------------------------------------------------------------- */
/**
 * Installation types that leave the paint in a state where ceramic
 * coating is the natural next step: full paint correction pass, full or
 * exterior detail (which include machine polishing), and full colour
 * changes on new wraps (bare film benefits from ceramic top-coat). Chrome
 * delete + partial wraps are intentionally excluded — the upsell would
 * feel forced with too little prep surface.
 */
const CERAMIC_PAIRABLE = new Set([
  "detail_paint_correction",
  "detail_full",
  "detail_exterior",
  "wrap_colour_change",
]);

function shouldSuggestCeramic(installationType, siblingTypes) {
  if (!CERAMIC_PAIRABLE.has(installationType)) return false;
  // Already on the item? Nothing to upsell.
  return !siblingTypes.includes("ceramic");
}

/* -------------------------------------------------------------------------- */
/* VehicleSizeChip                                                            */
/* -------------------------------------------------------------------------- */
/**
 * Compact "detected: SUV" pill under the year/make/model row. Debounced
 * so we don't hammer the API on every keystroke. Silently hides itself
 * if the classifier returns "unknown" — no noise when we can't help.
 */
function VehicleSizeChip({ make, model, itemType, index }) {
  const [guess, setGuess] = useState(null);
  useEffect(() => {
    const m = (make || "").trim();
    const md = (model || "").trim();
    if (!m && !md) { setGuess(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/quote-agent/vehicle-size", {
          make: m, model: md, item_type: itemType || "car",
        });
        // Only render when confidence is meaningful; keep the UI quiet
        // otherwise so an unknown model doesn't badge every quote.
        if (data && data.confidence >= 60 && data.size_class !== "unknown") {
          setGuess(data);
        } else {
          setGuess(null);
        }
      } catch { setGuess(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [make, model, itemType]);

  if (!guess) return null;
  return (
    <div
      data-testid={`item-${index}-size-chip`}
      className="flex items-center gap-2 text-[11px]"
    >
      <span className="text-slate-500 uppercase tracking-widest">Auto-classified</span>
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 px-2 py-0.5 text-emerald-200">
        {guess.label}
      </span>
      <span className="text-slate-600 text-[10px]">
        Sent to the AI so it picks the right base price band
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* AreaComboHint                                                              */
/* -------------------------------------------------------------------------- */
/**
 * "This combo was $1,850 last time" hint under the area picker. Silent
 * unless the admin has switched on Advanced Quoting → Area price memory
 * AND we have at least one invoiced match for the exact area set.
 */
function AreaComboHint({ jobType, installationType, areas, testId }) {
  const [hint, setHint] = useState(null);
  const [enabled, setEnabled] = useState(true);
  useEffect(() => {
    if (!enabled) return;
    if (!installationType || !areas || areas.length === 0) {
      setHint(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/quote-agent/area-price-hint", {
          job_type: jobType, installation_type: installationType, areas,
        });
        if (data.enabled === false) {
          setEnabled(false);  // stops all further calls this mount
          setHint(null);
          return;
        }
        setHint(data.match || null);
      } catch { setHint(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [jobType, installationType, areas, enabled]);

  if (!enabled || !hint) return null;
  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const relative = hint.last_at ? formatRelative(hint.last_at) : "recently";
  return (
    <div
      data-testid={testId}
      className="flex items-start gap-2 rounded-2xl bg-violet-500/10 border border-violet-400/30 p-3 text-xs text-violet-100"
    >
      <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-violet-500/25 border border-violet-400/40 flex items-center justify-center text-[10px] font-bold">$</div>
      <div className="flex-1">
        <div>
          <b>This combo was {money(hint.last_price)} last time</b>
          {hint.last_vehicle ? ` — ${hint.last_vehicle}, ${relative}` : ` — ${relative}`}
        </div>
        {hint.count > 1 && (
          <div className="text-violet-300/80 mt-0.5">
            Charged {hint.count}× · avg {money(hint.avg)} · range {money(hint.min)}–{money(hint.max)}
          </div>
        )}
      </div>
    </div>
  );
}

function formatRelative(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "recently";
    const diffDays = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (diffDays < 1) return "today";
    if (diffDays < 2) return "yesterday";
    if (diffDays < 30) return `${diffDays}d ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
  } catch { return "recently"; }
}
